import React, { useEffect, useState } from 'react'
import CustomModal from '../CustomModal/CustomModal'
import GameShowcaseForm from '../UpdateSiteModal/components/GameShowcase/GameShowcaseForm'
import { checkGameShowcaseEligibility } from '../../services/games'
import styles from './GameShowcaseModal.module.css'

const getSiteLabel = (site) => site?.pageTitle?.trim() || site?.subdomain

/** Lets the owner pick which of several eligible games they mean, when there's more than one -
 * shown instead of the form until a site is chosen. */
const GameSitePicker = ({ sites, onSelect }) => (
  <>
    <p className={styles.pickerHint}>You have more than one game ready for the showcase - which one?</p>
    <ul className={styles.pickerList}>
      {sites.map((site) => (
        <li key={site.subdomain}>
          <button type="button" className={styles.pickerItem} onClick={() => onSelect(site)}>
            <span className={styles.pickerItemTitle}>{getSiteLabel(site)}</span>
            {getSiteLabel(site) !== site.subdomain && <span className={styles.pickerItemDomain}>{site.subdomain}</span>}
          </button>
        </li>
      ))}
    </ul>
  </>
)

/**
 * `sites` is every eligible-and-unsubmitted game this could be for - usually one (from a specific
 * row's "Submit Game" button, which already knows exactly which game), but can be several (from
 * the page-level banner, which doesn't). With more than one, shows a picker step first instead of
 * silently guessing; with exactly one, goes straight to the form as before.
 */
const GameShowcaseModal = ({ show, onHide, sites = [] }) => {
  const [selectedSite, setSelectedSite] = useState(null)
  const [status, setStatus] = useState(null)

  // Only re-run when the modal opens, not on every re-render that hands us a new `sites` array
  // reference - re-opening onto a different game set (e.g. one gets submitted elsewhere) still
  // resets correctly since `show` cycles false -> true first.
  useEffect(() => {
    if (!show) return
    setSelectedSite(sites.length === 1 ? sites[0] : null)
  }, [show])

  const subdomain = selectedSite?.subdomain

  useEffect(() => {
    if (!show || !subdomain) return

    let cancelled = false
    setStatus(null)
    checkGameShowcaseEligibility(subdomain)
      .then(({ status: nextStatus }) => {
        if (!cancelled) setStatus(nextStatus || null)
      })
      .catch(() => {
        if (!cancelled) setStatus(null)
      })

    return () => {
      cancelled = true
    }
  }, [show, subdomain])

  if (!sites.length) return null

  const showingPicker = !selectedSite
  const canChangeSite = sites.length > 1 && !showingPicker

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      centered
      aria-labelledby="game-showcase-modal-title"
      className={styles.modal}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.title}>
            <i className="bi bi-controller" style={{ fontSize: 22 }} />
            <h2 id="game-showcase-modal-title">Join our Games Showcase</h2>
          </span>
          <button className={styles.closeButton} onClick={onHide} aria-label="Close modal" type="button">
            <img src="/assets/icons/cross.png" alt="close" width="15" height="15" />
          </button>
        </div>

        {showingPicker ? (
          <GameSitePicker sites={sites} onSelect={setSelectedSite} />
        ) : (
          <>
            <p className={styles.selectedSite}>
              Submitting <strong>{getSiteLabel(selectedSite)}</strong>
              {canChangeSite && (
                <button type="button" className={styles.changeSiteBtn} onClick={() => setSelectedSite(null)}>
                  Choose a different game
                </button>
              )}
            </p>
            <GameShowcaseForm
              subdomain={subdomain}
              status={status}
              onStatusChange={setStatus}
              categorySelectId="liveProjectsGameShowcaseCategory"
            />
          </>
        )}
      </div>
    </CustomModal>
  )
}

export default GameShowcaseModal
